/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { BlockTypes } from "constants/BlockTypes";
import { ClipUploadStates } from "constants/ClipUploadStates";
import {
  AssetId,
  ClipId,
  ClipEnhancementInfo,
  ClipParticipant,
} from "@phonos/types";
import { MediaType } from "@phonos/types";
export type { ClipId, ClipEnhancementInfo, AssetId };

/*
 * duration and offset are segment of a clip
 * relative to the clip track
 */
export interface ClipBase {
  type: BlockTypes;
  id: ClipId;
  duration: number;
  offset: number;
}

/*
 * Clips with Asset Support
 */
type PendingAssetInfo = {
  uploadState: ClipUploadStates.PENDING;
  assetId: AssetId | null;
};
export type UploadedAssetInfo = {
  uploadState: ClipUploadStates.COMPLETE;
  assetId: AssetId;
};
export type UploadingAssetInfo = {
  uploadState: ClipUploadStates.STARTED;
  name: string;
  assetId: AssetId | null;
};
export type ProcessingAssetInfo = {
  uploadState: ClipUploadStates.PROCESSING;
  assetId: null;
};
type FailedUploadAssetInfo = {
  uploadState: ClipUploadStates.FAILED;
  assetId: AssetId | null;
};
type FailedProcessingAssetInfo = {
  uploadState: ClipUploadStates.FAILED_PROCESSING;
  assetId: AssetId | null;
};
type AssetInfoForClip =
  | PendingAssetInfo
  | UploadedAssetInfo
  | UploadingAssetInfo
  | ProcessingAssetInfo
  | FailedUploadAssetInfo
  | FailedProcessingAssetInfo;

export type WaveformClip = ClipBase &
  AssetInfoForClip & {
    type: BlockTypes.WAVEFORM;
    name?: string;
    isBackground: boolean;
    backgroundGain?: number;
    fadeIn: boolean;
    fadeOut: boolean;
  };

export type TranscriptionClip = ClipBase &
  AssetInfoForClip & {
    type: BlockTypes.TRANSCRIPTION;
    displayName: string | undefined;

    // Prefix that is combined with a token ID from an asset transcription to create
    // a unique token id for the whole project.
    // Unlike, the clip id, the clip prefix will not change under clip splits (from an
    // insertion or deletion, TrimUtilities.ts) to keep the token ids stable across
    // edits.
    tokenIdPrefix: string;
  };

export type RecordingSessionClip = ClipBase &
  AssetInfoForClip & {
    type: BlockTypes.RECORDING_SESSION;
  };

/*
 * Clips without Asset Support
 */
export type PlaceholderClip = ClipBase & {
  type: BlockTypes.PLACEHOLDER;
  text: string | null;
  duration: 0;
  offset: 0;
};

export type ClipWithAssetSupport =
  | WaveformClip
  | TranscriptionClip
  | RecordingSessionClip;
export type ClipWithAsset = ClipWithAssetSupport & UploadedAssetInfo;
export const isClipTypeWithAssetSupport = (
  clip: Clip
): clip is ClipWithAssetSupport =>
  [
    BlockTypes.WAVEFORM,
    BlockTypes.TRANSCRIPTION,
    BlockTypes.RECORDING_SESSION,
  ].includes(clip.type);
export type ClipWithoutAssetSupport = PlaceholderClip;
export const isClipTypeWithoutAssetSupport = (
  clip: Clip
): clip is ClipWithoutAssetSupport => clip.type === BlockTypes.PLACEHOLDER;
export const isClipWithAsset = (clip: Clip): clip is ClipWithAsset => {
  const hasAsset =
    isClipTypeWithAssetSupport(clip) &&
    clip.uploadState === ClipUploadStates.COMPLETE;
  if (!hasAsset) {
    return false;
  }
  console.assert(
    clip.assetId,
    "Clip with missing assetId in the ClipUploadStates.COMPLETE state"
  );
  return Boolean(clip.assetId);
};

export const isClipUploading = (
  clip: Clip
): clip is ClipWithAssetSupport & UploadingAssetInfo => {
  return (
    isClipTypeWithAssetSupport(clip) &&
    clip.uploadState === ClipUploadStates.STARTED
  );
};

/*
 * Clips
 */
export type Clip =
  | WaveformClip
  | PlaceholderClip
  | TranscriptionClip
  | RecordingSessionClip;

/*
 * Timeline positioning of a clip
 */
export type ClipWithPlaylistTimes = Clip & {
  startTime: number;
  endTime: number;
};

export type PlaylistClip = ClipWithPlaylistTimes &
  ClipWithAsset & {
    enhancementInfo?: ClipEnhancementInfo;
    mediaType: MediaType;
    participants?: ClipParticipant[];
  };

export type ClipCopy = Omit<Clip, "id">;

export enum ClipEffect {
  fadeIn = "fadeIn",
  fadeOut = "fadeOut",
}
